import { useRef, useState } from 'react';
import cn from 'classnames';
import { StripeCapitalFaq } from 'slickpay/capital/StripeCapitalFaq.component';

import IMG_ABOUT from 'assets/slickpay-splash/stripe-capital/about.png';
import IMG_OFFER from 'assets/slickpay-splash/stripe-capital/my-offer.png';
import ExternalLink from 'components/generic/ExternalLink.component';
import Video from 'components/generic/Video.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import S from 'constants/string.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import { useTranslation } from 'providers/GILT.provider';

import TopNav from 'common/TopNav.component';

import CN from './StripeCapitalSplash.module.scss';

const VIDEO_SOURCE = 'https://www.loom.com/embed/c795a395228f4d1d8c6953c6dbfaff04';
const LINK_STRIPE_LOGIN = 'https://connect.stripe.com/internal_express_login/';

const FEATURE_IDS = ['revenue', 'fee', 'repay'];
const USE_CASE_IDS = ['equipment', 'refurbish', 'team', 'stock', 'marketing', 'location'];
const USE_CASE_ICONS = {
  equipment: S.womanHaircut,
  refurbish: S.refurbish,
  team: S.womanTipping,
  stock: S.stock,
  marketing: S.megaphone,
  location: S.location,
};

const STEP_IDS = ['offer', 'amount', 'funded'];

const LOCATIONS_ITEMS = ['reports', 'kpi', 'settings'];

const TEAM_ITEMS = [
  { type: 'text', key: 'managers' },
  { type: 'text', key: 'stylists' },
  {
    type: 'emphasis',
    key: 'dontWorry',
  },
];

export const StripeCapitalSplash = () => {
  const { t } = useTranslation();
  const isMobileViewport = useIsMobileViewport();

  const [openFaqQuestion, setOpenFaqQuestion] = useState('');
  const [faqScrollCount, setFaqScrollCount] = useState(0);
  const howRef = useRef(null);

  const handleScrollToHow = (e) => {
    e.preventDefault();
    howRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleScrollToFaq = (e) => {
    setOpenFaqQuestion('increaseChances');
    setFaqScrollCount((currentFaqScrollCount) => currentFaqScrollCount + 1);
  };

  return (
    <div
      data-bem="StripeCapitalSplash__component"
      className={cn({
        [CN.component]: true,

        [CN.isMobile]: isMobileViewport,
      })}
    >
      <TopNav title={t('stripeCapitalSplash.navTitle')}>
        <SidebarMenu />
      </TopNav>

      <section className={CN.hero}>
        <div className={CN.heroBadge}>
          <span>{S.rocket}</span>
          {t('stripeCapitalSplash.hero.badge')}
        </div>
        <h1 className={CN.heroTitle}>
          {t('stripeCapitalSplash.hero.title')} <span className={CN.heroAccent}>{t('stripeCapitalSplash.hero.titleAccent')}</span>
        </h1>
        <p className={CN.heroSubtitle}>{t('stripeCapitalSplash.hero.subtitle')}</p>
        <div className={CN.heroCtas}>
          <ExternalLink variant="secondaryCard" onClick={handleScrollToHow}>
            {t('stripeCapitalSplash.hero.howItWorks')}
          </ExternalLink>
        </div>
      </section>

      <section className={CN.about}>
        <div className={CN.aboutInner}>
          <div>
            <div className={CN.sectionBadge}>{t('stripeCapitalSplash.about.badge')}</div>
            <h2 className={CN.aboutTitle}>{t('stripeCapitalSplash.about.title')}</h2>
            <p className={CN.aboutDescription}>{t('stripeCapitalSplash.about.description')}</p>

            <small className={CN.aboutVideo}>{t('stripeCapitalSplash.about.aboutVideo')}</small>
          </div>
          <div className={CN.imageAbout}>
            <img src={IMG_ABOUT} className={CN.img} alt={t('stripeCapitalSplash.about.title')} />
          </div>
        </div>
      </section>

      <section className={CN.features}>
        <div className={CN.featuresInner}>
          {FEATURE_IDS.map((id) => (
            <div key={id} className={CN.featureCard}>
              <span className={CN.featureCheck}>{S.checkmark}</span>
              <div>
                <div className={CN.featureTitle}>{t(`stripeCapitalSplash.features.${id}.title`)}</div>
                <p className={CN.featureText}>{t(`stripeCapitalSplash.features.${id}.text`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className={CN.banner}>
        <CtaBanner1 onScrollToHow={handleScrollToHow} onScrollToFaq={handleScrollToFaq} />
      </section>

      <section className={CN.useCases}>
        <div className={CN.useCasesInner}>
          <h2 className={CN.sectionTitle}>{t('stripeCapitalSplash.useCases.title')}</h2>
          <p className={CN.sectionSubtitle}>{t('stripeCapitalSplash.useCases.subtitle')}</p>
          <div className={CN.useCasesGrid}>
            {USE_CASE_IDS.map((id) => (
              <div key={id} className={CN.useCaseCard}>
                <div className={CN.useCaseIcon}>{USE_CASE_ICONS[id]}</div>
                <div className={CN.useCaseTitle}>{t(`stripeCapitalSplash.useCases.${id}.title`)}</div>
                <p className={CN.useCaseText}>{t(`stripeCapitalSplash.useCases.${id}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={CN.howItWorks} ref={howRef}>
        <div className={CN.howInner}>
          <div>
            <div className={CN.sectionBadge}>{t('stripeCapitalSplash.howItWorks.badge')}</div>
            <h2 className={CN.howTitle}>{t('stripeCapitalSplash.howItWorks.title')}</h2>
            <div className={CN.steps}>
              {STEP_IDS.map((id, i) => (
                <div key={id} className={CN.step}>
                  <div className={CN.stepNumber}>{i + 1}</div>
                  <div>
                    <div className={CN.stepTitle}>{t(`stripeCapitalSplash.howItWorks.${id}.title`)}</div>
                    <p className={CN.stepText}>{t(`stripeCapitalSplash.howItWorks.${id}.text`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className={CN.chartPlaceholder}>
            <div className={CN.chartArea}>
              <Video src={VIDEO_SOURCE} title={t('stripeCapitalSplash.howItWorks.chart.title')} variant="full" />
            </div>
          </div>
        </div>
      </section>

      <section className={CN.offerExample}>
        <div className={CN.offerExampleInner}>
          <div className={CN.offerExampleImage}>
            <img src={IMG_OFFER} className={CN.img} alt={t('stripeCapitalSplash.offerExample.title')} />
          </div>
          <div>
            <h2 className={CN.offerExampleTitle}>{t('stripeCapitalSplash.offerExample.title')}</h2>
            <p className={CN.offerExampleIntro}>{t('stripeCapitalSplash.offerExample.intro')}</p>
            <ul className={CN.offerExampleList}>
              {LOCATIONS_ITEMS.map((key) => (
                <li key={key}>{t(`stripeCapitalSplash.offerExample.locations.${key}`)}</li>
              ))}
            </ul>
            <h3 className={CN.offerExampleSubTitle}>{t('stripeCapitalSplash.offerExample.teamTitle')}</h3>
            <ul className={CN.offerExampleList}>
              {TEAM_ITEMS.map((item) => {
                if (item.type === 'emphasis') {
                  return (
                    <li key={item.key} className={CN.offerExampleEmphasis}>
                      <strong className={CN.bold}>{t(`stripeCapitalSplash.offerExample.team.${item.key}Emphasis`)}</strong>
                      {t(`stripeCapitalSplash.offerExample.team.${item.key}Text`)}
                    </li>
                  );
                }

                return <li key={item.key}>{t(`stripeCapitalSplash.offerExample.team.${item.key}`)}</li>;
              })}
            </ul>
          </div>
        </div>
      </section>

      <section className={CN.banner}>
        <CtaBanner2 onScrollToFaq={handleScrollToFaq} />
      </section>

      <section className={CN.faqContainer}>
        <StripeCapitalFaq isDark openQuestionKey={openFaqQuestion} scrollTrigger={faqScrollCount} />
      </section>
    </div>
  );
};

const CtaBanner1 = ({ onScrollToFaq, onScrollToHow }) => {
  const { t } = useTranslation();

  return (
    <div className={CN.ctaBanner}>
      <div className={CN.ctaInner}>
        <h2 className={CN.ctaTitle}>{t('stripeCapitalSplash.cta.title')}</h2>
        <p className={CN.ctaLoginText}>{t('stripeCapitalSplash.cta.loginText')}</p>
        <p className={CN.ctaMeta}>
          {t('stripeCapitalSplash.cta.metaPrefix')}

          <button type="button" onClick={onScrollToFaq} className={CN.metaLink}>
            {t('stripeCapitalSplash.cta.metaLink')}
          </button>

          {t('stripeCapitalSplash.cta.metaSuffix')}
        </p>

        <ExternalLink variant="primaryCard" onClick={onScrollToHow}>
          {t('stripeCapitalSplash.cta.button')}
        </ExternalLink>
      </div>
    </div>
  );
};

const CtaBanner2 = ({ onScrollToFaq }) => {
  const { t } = useTranslation();
  return (
    <div className={CN.ctaBanner}>
      <div className={CN.ctaInner}>
        <h2 className={CN.ctaTitle}>{t('stripeCapitalSplash.cta.title')}</h2>
        <p className={CN.ctaLoginText}>{t('stripeCapitalSplash.cta.loginText')}</p>
        <p className={CN.ctaMeta}>
          {t('stripeCapitalSplash.cta.metaPrefix')}

          <button type="button" onClick={onScrollToFaq} className={CN.metaLink}>
            {t('stripeCapitalSplash.cta.metaLink')}
          </button>

          {t('stripeCapitalSplash.cta.metaSuffix')}
        </p>

        <ExternalLink variant="primaryCard" link={LINK_STRIPE_LOGIN}>
          {t('stripeCapitalSplash.cta.linkStripe')}
        </ExternalLink>
      </div>
    </div>
  );
};
